import React, { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Trash2, Search, X, Download, Upload, FileText, Send, CheckCircle, Clock, Save, Edit3, ArrowLeft, AlertTriangle, Ban } from 'lucide-react';
import DocumentosClienteBox from '../../components/shared/DocumentosClienteBox';
import { incorporadoraDe } from '../../utils/incorporadora';
import { formatUnidade } from '../../utils/unidade';

// PDF assinado disponível: Clicksign concluído OU contrato legado (PDF guardado no servidor)
const temPdfAssinado = (c) => !!c && (['closed', 'finished'].includes(c.clicksign_status) || !!c.arquivo_assinado_pdf);
const ehLegado = (c) => c?.origem === 'legado';

// ── Download autenticado via axios (inclui o header Authorization automaticamente) ─
async function downloadDocx(apiInstance, relativePath, nomeArquivo) {
  try {
    const res = await apiInstance.get(relativePath, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = nomeArquivo || 'contrato.docx';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  } catch (err) {
    alert('Erro ao baixar o arquivo: ' + (err.response?.data?.error || err.message));
  }
}

// nome de arquivo "Minuta_<unidade><torre>_<primeiro nome do cliente>" — mesmo padrão usado
// no backend (reservas.controller.js) e no jeito que o Fernando organiza as minutas manualmente
function nomeMinutaArquivo(c) {
  const unidade = `${c.unidade_numero || ''}${(c.torre || '').replace(/^torre\s*/i, '').trim()}`.replace(/[^A-Za-z0-9]+/g, '') || 'Unidade';
  const primeiroNomeRaw = String(c.cliente_nome_arquivo || c.cliente_nome || '').trim().split(/\s+/)[0]?.replace(/[^A-Za-z0-9]+/g, '') || 'Cliente';
  const primeiroNome = primeiroNomeRaw ? primeiroNomeRaw[0].toUpperCase() + primeiroNomeRaw.slice(1).toLowerCase() : primeiroNomeRaw;
  return `Minuta_${unidade}_${primeiroNome}.docx`;
}

// ── Helpers ───────────────────────────────────────────────────────
function fmtBRL(val) {
  if (!val && val !== 0) return '—';
  return parseFloat(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function fmtData(str) {
  if (!str) return '—';
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) { const [y,m,d] = str.split('-'); return `${d}/${m}/${y}`; }
  try { return new Date(str).toLocaleDateString('pt-BR'); } catch { return str || '—'; }
}

// ── Signatários Clicksign: leitura tolerante do JSON gravado em contratos.clicksign_signatarios
// (o webhook /api/webhooks/clicksign mantém o signed_at de cada um atualizado em tempo real) ──
function parseSignatarios(c) {
  let s = c?.clicksign_signatarios;
  if (!s) return [];
  if (typeof s === 'string') { try { s = JSON.parse(s); } catch { return []; } }
  return Array.isArray(s) ? s : [];
}
const PAPEL_LABEL = {
  assinatura_vendedora: 'Vendedora',
  assinatura_comprador: 'Comprador',
  assinatura_comprador1: 'Comprador',
  assinatura_comprador2: 'Comprador 2',
  assinatura_conjuge: 'Cônjuge',
  assinatura_testemunha1: 'Testemunha — Corretor',
  assinatura_testemunha2: 'Testemunha — Inbase',
  sign: 'Assinante',
  witness: 'Testemunha',
};
function papelLabel(papel) {
  if (!papel) return 'Assinante';
  if (PAPEL_LABEL[papel]) return PAPEL_LABEL[papel];
  return String(papel).replace(/^(assinatura|rubrica)_/, '').replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());
}
function primeiroNome(nome) {
  return String(nome || '').trim().split(/\s+/)[0] || '—';
}
function fmtTel(t) {
  const d = String(t || '').replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return t || '';
}
const isoDay = (v) => (v ? String(v).slice(0, 10) : '');
// soma n meses a uma data ISO (YYYY-MM-DD) mantendo o dia de vencimento
function addMesesISO(iso, n, dia) {
  const base = iso ? new Date(`${isoDay(iso)}T00:00:00Z`) : null;
  if (!base || Number.isNaN(base.getTime())) return '';
  const alvo = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + n, 1));
  const d = dia || base.getUTCDate();
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  alvo.setUTCDate(Math.min(d, ultimo));
  return alvo.toISOString().slice(0, 10);
}
const TIPO_PARCELA_OPCOES = [
  ['entrada', 'Entrada'], ['ato', 'Ato'], ['fluxo', 'Parcela'], ['balao', 'Balão'], ['saldo', 'Saldo devedor'], ['comissao', 'Comissão'], ['outro', 'Outro'],
];

// ── Editor do cronograma de boletos (reserva_parcelas) — dentro do modal de Contrato ──
function CronogramaBoletos({ contratoId }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState(null); // null = ainda não carregou
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['contrato-parcelas', contratoId],
    queryFn: () => api.get(`contratos/${contratoId}/parcelas`).then(r => r.data),
  });

  const semReserva = data && data.reserva_id === null;
  const base = React.useMemo(() => (data?.data || []).map(p => ({
    id: p.id, // o backend casa pela id e preserva boleto/pagamento já gravado na parcela
    tipo: p.tipo || 'fluxo', descricao: p.descricao || '', numero: p.numero ?? '', total: p.total ?? '',
    valor: p.valor != null ? String(p.valor) : '', vencimento: isoDay(p.vencimento), forma: p.forma || '',
  })), [data]);
  const lista = rows ?? base;
  const setLista = (next) => setRows(typeof next === 'function' ? next(lista) : next);

  const addLinha = () => setLista(l => [...l, { tipo: 'fluxo', descricao: '', numero: '', total: '', valor: '', vencimento: '', forma: 'Boleto bancário' }]);
  const rmLinha = (i) => setLista(l => l.filter((_, k) => k !== i));
  const setCampo = (i, campo, v) => setLista(l => l.map((row, k) => k === i ? { ...row, [campo]: v } : row));

  const gerarDoResumo = () => {
    const r = data?.resumo_reserva || {};
    const out = [];
    if (Number(r.entrada_valor) > 0 || r.entrada_vencimento) out.push({ tipo: 'entrada', descricao: 'Entrada', numero: '', total: '', valor: r.entrada_valor != null ? String(r.entrada_valor) : '', vencimento: isoDay(r.entrada_vencimento), forma: r.entrada_forma || 'Boleto bancário' });
    if (Number(r.ato_valor) > 0 || r.ato_data) out.push({ tipo: 'ato', descricao: 'Ato', numero: '', total: '', valor: r.ato_valor != null ? String(r.ato_valor) : '', vencimento: isoDay(r.ato_data), forma: 'Boleto bancário' });
    const mq = Number(r.mensais_qtd) || 0;
    if (mq > 0) {
      const primeira = isoDay(r.mensais_primeira_data);
      const dia = r.mensais_dia_vencimento || (primeira ? Number(primeira.slice(8, 10)) : null);
      for (let i = 0; i < mq; i++) out.push({ tipo: 'fluxo', descricao: 'Parcela mensal', numero: i + 1, total: mq, valor: r.mensais_valor != null ? String(r.mensais_valor) : '', vencimento: i === 0 ? primeira : addMesesISO(primeira, i, dia), forma: 'Boleto bancário' });
    }
    const bq = Number(r.baloes_qtd) || 0;
    if (bq > 0) {
      const primeira = isoDay(r.baloes_primeira_data);
      const passo = { mensal: 1, bimestral: 2, trimestral: 3, quadrimestral: 4, semestral: 6, anual: 12 }[String(r.baloes_frequencia || '').toLowerCase()] || 6;
      for (let i = 0; i < bq; i++) out.push({ tipo: 'balao', descricao: `Balão ${r.baloes_frequencia || ''}`.trim(), numero: i + 1, total: bq, valor: r.baloes_valor != null ? String(r.baloes_valor) : '', vencimento: addMesesISO(primeira, i * passo, primeira ? Number(primeira.slice(8, 10)) : null), forma: 'Boleto bancário' });
    }
    if (Number(r.saldo_devedor) > 0) out.push({ tipo: 'saldo', descricao: 'Saldo devedor (nas chaves)', numero: '', total: '', valor: String(r.saldo_devedor), vencimento: '', forma: '' });
    // parcelas de comissão não saem do resumo — mantém as atuais (podem ter boleto LinkPay emitido)
    setLista([...lista.filter(p => p.tipo === 'comissao'), ...out]);
    setMsg('Gerado do resumo da reserva — revise as datas e clique em Salvar.');
  };

  const salvar = async () => {
    setSaving(true); setMsg('');
    try {
      await api.put(`contratos/${contratoId}/parcelas`, { parcelas: lista });
      setRows(null);
      qc.invalidateQueries(['contrato-parcelas', contratoId]);
      setMsg('Cronograma salvo. É isso que o aviso ao financeiro vai usar.');
    } catch (err) {
      setMsg('Erro ao salvar: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const total = lista.reduce((s, p) => s + (parseFloat(String(p.valor).replace(/\./g, '').replace(',', '.')) || 0), 0);

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cronograma de boletos (cobrança)</h3>
        {!semReserva && (
          <div className="flex items-center gap-2">
            <button onClick={gerarDoResumo} className="text-xs text-blue-600 hover:text-blue-800 transition">Gerar do resumo da reserva</button>
            <button onClick={addLinha} className="text-xs text-blue-600 hover:text-blue-800 transition">+ Linha</button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-xs text-gray-400 py-2">Carregando...</div>
      ) : semReserva ? (
        <div className="text-xs text-gray-400 bg-gray-50 rounded-xl p-4">Este contrato não tem reserva vinculada — o cronograma vem da proposta.</div>
      ) : (
        <div className="bg-gray-50 rounded-xl p-3 space-y-2">
          <p className="text-[11px] text-gray-400">Alimenta o aviso ao financeiro. Se ficar vazio, o aviso usa o resumo da reserva (ato / mensais / saldo).</p>
          {lista.length === 0 && <div className="text-xs text-gray-400 py-1">Nenhuma parcela. Use "Gerar do resumo da reserva" ou "+ Linha".</div>}
          {lista.map((p, i) => (
            <div key={i} className="grid grid-cols-[84px_1fr_54px_92px_128px_28px] gap-1.5 items-center bg-white rounded-lg p-1.5 border border-gray-100">
              <select value={p.tipo} onChange={e => setCampo(i, 'tipo', e.target.value)} className="text-xs border border-gray-200 rounded px-1 py-1">
                {TIPO_PARCELA_OPCOES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input value={p.descricao} onChange={e => setCampo(i, 'descricao', e.target.value)} placeholder="Descrição" className="text-xs border border-gray-200 rounded px-1.5 py-1"/>
              <input value={p.numero} onChange={e => setCampo(i, 'numero', e.target.value)} placeholder="nº" className="text-xs border border-gray-200 rounded px-1 py-1 text-center"/>
              <input value={p.valor} onChange={e => setCampo(i, 'valor', e.target.value)} placeholder="Valor" className="text-xs border border-gray-200 rounded px-1.5 py-1"/>
              <input type="date" value={p.vencimento} onChange={e => setCampo(i, 'vencimento', e.target.value)} className="text-xs border border-gray-200 rounded px-1 py-1"/>
              <button onClick={() => rmLinha(i)} className="text-red-400 hover:text-red-600 flex justify-center"><Trash2 size={13}/></button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[11px] text-gray-500">Soma: <strong>{fmtBRL(total)}</strong></span>
            <button onClick={salvar} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-primary rounded-lg hover:bg-primary-dark transition disabled:opacity-50">
              <Save size={12}/> {saving ? 'Salvando...' : 'Salvar cronograma'}
            </button>
          </div>
          {msg && <div className="text-[11px] text-blue-700">{msg}</div>}
        </div>
      )}
    </section>
  );
}

// ── Linha de um signatário no painel de assinaturas ──────────────────────────────
function SignatarioRow({ s, contratoId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [tel, setTel] = useState(s.whatsapp || '');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const reenviar = async () => {
    setBusy('reenviar'); setErr('');
    try {
      const r = await api.post(`contratos/${contratoId}/clicksign-reenviar`, { signer_key: s.signer_key });
      onChanged?.(`Notificação reenviada para ${r.data.data?.notificado || s.nome}.`);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(''); }
  };

  const salvarNumero = async () => {
    setBusy('salvar'); setErr('');
    try {
      const r = await api.patch(`contratos/${contratoId}/clicksign-signatario`, { signer_key: s.signer_key, whatsapp: tel });
      const d = r.data.data || {};
      const partes = [`Número de ${d.signatario || s.nome} atualizado no Clicksign`];
      if (d.cadastro?.ok) partes.push(`e no cadastro (${d.cadastro.alvo})`);
      partes.push(d.reenviado ? '— notificação reenviada.' : '— a notificação falhou, use "Reenviar".');
      setEditing(false);
      onChanged?.(partes.join(' '));
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(''); }
  };

  return (
    <div className={`px-3 py-2 rounded-lg text-sm ${s.signed_at ? 'bg-green-50' : 'bg-white border border-gray-100'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {s.signed_at
            ? <CheckCircle size={15} className="text-green-500 shrink-0"/>
            : <Clock size={15} className="text-gray-300 shrink-0"/>}
          <div className="min-w-0">
            <div className="font-medium text-gray-800 text-xs truncate">{s.nome}</div>
            <div className="text-gray-400 text-xs truncate">{papelLabel(s.papel)}{s.whatsapp ? ` · ${fmtTel(s.whatsapp)}` : ''}</div>
          </div>
        </div>
        <div className="text-right text-xs flex items-center gap-1 shrink-0">
          {s.signed_at ? (
            <span className="text-green-600 font-semibold">{fmtData(s.signed_at)}</span>
          ) : (
            <>
              <button onClick={reenviar} disabled={!!busy} title="Reenviar notificação de assinatura"
                className="flex items-center gap-1 px-2 py-1 rounded-md text-blue-600 hover:bg-blue-50 transition disabled:opacity-50">
                <Send size={12}/> {busy === 'reenviar' ? '...' : 'Reenviar'}
              </button>
              <button onClick={() => { setEditing(v => !v); setTel(s.whatsapp || ''); setErr(''); }} disabled={!!busy}
                title="Corrigir o WhatsApp deste signatário"
                className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition">
                <Edit3 size={12}/>
              </button>
            </>
          )}
        </div>
      </div>

      {editing && !s.signed_at && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input value={tel} onChange={e => setTel(e.target.value)} placeholder="DDD + número (ex.: 44 99999-9999)"
            className="flex-1 min-w-[180px] text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"/>
          <button onClick={salvarNumero} disabled={busy === 'salvar' || !tel.trim()}
            className="text-xs font-bold text-white bg-blue-600 rounded-md px-3 py-1.5 hover:bg-blue-700 transition disabled:opacity-50">
            {busy === 'salvar' ? 'Salvando...' : 'Salvar e reenviar'}
          </button>
          <button onClick={() => setEditing(false)} className="text-xs text-gray-500 px-2 py-1.5">Cancelar</button>
        </div>
      )}
      {err && <div className="mt-1.5 text-xs text-red-600">{err}</div>}
    </div>
  );
}

// ── Painel de progresso de assinaturas ───────────────────────────
function SignatariosPanel({ contratoId, clicksignKey, signatariosIniciais = [], clicksignStatus, numeroContrato }) {
  const [refreshing, setRefreshing] = useState(false);
  const [aviso, setAviso] = useState('');
  const [baixando, setBaixando] = useState(false);
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['clicksign-status', contratoId],
    queryFn: () => api.get(`contratos/${contratoId}/clicksign-status`).then(r => r.data.data),
    enabled: !!clicksignKey,
    staleTime: 30000,
    // mostra na hora o que já está salvo no contrato (mantido pelo webhook) enquanto busca o status ao vivo
    placeholderData: signatariosIniciais.length ? { signatarios: signatariosIniciais } : undefined,
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    qc.invalidateQueries(['contrato', contratoId]);
    qc.invalidateQueries(['contratos']);
    setRefreshing(false);
  };

  const afterChange = async (msg) => {
    setAviso(msg);
    await refetch();
    qc.invalidateQueries(['contrato', contratoId]);
    qc.invalidateQueries(['contratos']);
  };

  const signatarios = data?.signatarios || [];
  const assinaram = signatarios.filter(s => s.signed_at).length;
  const total = signatarios.length;
  const pct = total > 0 ? Math.round((assinaram / total) * 100) : 0;
  const tudo_assinado = assinaram === total && total > 0;
  const statusAtual = data?.status || clicksignStatus;
  const finalizado = ['closed', 'finished'].includes(statusAtual) || tudo_assinado;

  const baixarAssinado = async () => {
    setBaixando(true);
    await downloadDocx(api, `contratos/${contratoId}/clicksign-assinado`, `${numeroContrato || 'contrato'}-assinado.pdf`);
    setBaixando(false);
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Assinaturas Clicksign</h3>
        <button onClick={handleRefresh} disabled={refreshing || isLoading}
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 transition">
          <svg className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      {/* Barra de progresso */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="font-medium text-gray-700">{assinaram} de {total} {total === 1 ? 'assinatura' : 'assinaturas'}</span>
          {tudo_assinado
            ? <span className="text-green-600 font-bold text-xs flex items-center gap-1"><CheckCircle size={12}/> Concluído</span>
            : <span className="text-orange-500 text-xs font-medium flex items-center gap-1"><Clock size={12}/> Aguardando</span>}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className={`h-2 rounded-full transition-all duration-500 ${tudo_assinado ? 'bg-green-500' : 'bg-blue-500'}`}
            style={{ width: `${pct}%` }} />
        </div>

        {finalizado && (
          <button onClick={baixarAssinado} disabled={baixando}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition disabled:opacity-50">
            <Download size={13}/> {baixando ? 'Baixando...' : 'Baixar contrato assinado (PDF)'}
          </button>
        )}

        {aviso && (
          <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">{aviso}</div>
        )}

        {/* Lista de signatários */}
        {isLoading ? (
          <div className="text-xs text-gray-400 text-center py-2">Carregando status...</div>
        ) : (
          <div className="space-y-2 mt-3">
            {signatarios.map((s, i) => (
              <SignatarioRow key={s.signer_key || i} s={s} contratoId={contratoId} onChanged={afterChange} />
            ))}
            {signatarios.length === 0 && (
              <div className="text-xs text-gray-400 text-center py-2">Nenhum signatário carregado — clique em "Atualizar"</div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Modal de visualização e edição do contrato ────────────────────
// Resumo de contrato legado (assinado fora do sistema): dados lidos do contrato na importação
// (contratos.resumo). Mostra o essencial pra equipe sem precisar abrir o PDF.
function ResumoLegado({ resumo }) {
  const [verAlertas, setVerAlertas] = useState(false);
  const r = typeof resumo === 'string' ? (() => { try { return JSON.parse(resumo); } catch { return null; } })() : resumo;
  if (!r) return null;
  const brl = (v) => (v == null ? null : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
  const dt = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10).split('-').reverse().join('/') : s);
  const Linha = ({ k, v }) => (v == null || v === '' ? null : (
    <div className="flex gap-3 py-1 border-b border-gray-100 last:border-0">
      <span className="w-40 shrink-0 text-xs text-gray-400">{k}</span><span className="text-sm text-gray-800">{v}</span>
    </div>
  ));
  const re = r.reajuste || {};
  const inter = r.intermediacao || {};
  const corretores = (inter.corretores || []).map((x) => `${x.nome}${x.creci ? ` (CRECI ${x.creci})` : ''}`).join(', ');
  return (
    <>
      <section>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Resumo do contrato</h3>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{r.resumo_texto}</div>
      </section>
      <section>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Comprador(es)</h3>
        <div className="space-y-2">
          {(r.compradores || []).map((p, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-3 text-sm">
              <div className="font-semibold">{p.nome} <span className="font-normal text-gray-500">— {p.tipo === 'pj' ? `CNPJ ${p.cnpj || '—'}` : `CPF ${p.cpf || '—'}`}</span></div>
              <div className="text-xs text-gray-500">{[p.rg && `RG ${p.rg}`, p.estado_civil, p.regime_bens, p.profissao].filter(Boolean).join(' · ')}</div>
              <div className="text-xs text-gray-500">{[p.endereco, p.cidade_uf, p.cep && `CEP ${p.cep}`].filter(Boolean).join(' — ')}</div>
              <div className="text-xs text-gray-500">{[p.email, p.telefone].filter(Boolean).join(' · ')}</div>
              {p.representante?.nome && <div className="text-xs text-gray-500">Representante: {p.representante.nome}{p.representante.cpf ? ` (CPF ${p.representante.cpf})` : ''}</div>}
            </div>
          ))}
          {r.conjuge?.nome && <div className="bg-gray-50 rounded-xl p-3 text-sm"><span className="text-xs text-gray-400">Cônjuge: </span><span className="font-semibold">{r.conjuge.nome}</span>{r.conjuge.cpf ? <span className="text-gray-500"> — CPF {r.conjuge.cpf}</span> : null}</div>}
        </div>
      </section>
      <section>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Valores e pagamento</h3>
        <div className="bg-gray-50 rounded-xl px-4 py-2">
          <Linha k="Preço total" v={brl(r.preco_total)} />
          <Linha k="Entrada" v={[brl(r.entrada?.valor), r.entrada?.forma].filter(Boolean).join(' — ')} />
          {(r.parcelas || []).map((p, i) => (
            <Linha key={i} k={`Parcelas${(r.parcelas || []).length > 1 ? ` (${i + 1})` : ''}`}
              v={[p.quantidade && brl(p.valor) ? `${p.quantidade}× ${brl(p.valor)}` : brl(p.valor), p.descricao,
                p.primeiro_vencimento && `de ${dt(p.primeiro_vencimento)}`, p.ultimo_vencimento && `até ${dt(p.ultimo_vencimento)}`].filter(Boolean).join(' · ')} />
          ))}
          <Linha k="Saldo devedor" v={[brl(r.saldo_devedor?.valor), r.saldo_devedor?.vencimento].filter(Boolean).join(' — ')} />
          <Linha k="Cobrança" v={r.forma_cobranca} />
        </div>
      </section>
      <section>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Reajuste, prazos e corretagem</h3>
        <div className="bg-gray-50 rounded-xl px-4 py-2">
          <Linha k="Índice na obra" v={[re.indice_obra, re.juros_obra && `juros ${re.juros_obra}`].filter(Boolean).join(' + ')} />
          <Linha k="Índice pós-chaves" v={[re.indice_pos_chaves, re.juros_pos_chaves && `juros ${re.juros_pos_chaves}`].filter(Boolean).join(' + ')} />
          <Linha k="Inadimplemento" v={r.inadimplemento} />
          <Linha k="Data do contrato" v={dt(r.data_contrato)} />
          <Linha k="Habite-se previsto" v={dt(r.habite_se_previsto)} />
          <Linha k="Entrega prevista" v={dt(r.entrega_prevista)} />
          <Linha k="Corretagem" v={[brl(r.corretagem?.valor), r.corretagem?.responsavel && `paga por ${r.corretagem.responsavel}`, !r.corretagem?.valor && r.corretagem?.texto].filter(Boolean).join(' — ')} />
          <Linha k="Intermediação" v={[corretores, inter.imobiliaria?.nome].filter(Boolean).join(' / ') || (inter.houve ? 'não identificada no contrato' : 'sem intermediação')} />
          <Linha k="Unidade" v={[r.unidade?.vagas && `vagas ${r.unidade.vagas}`, r.unidade?.area_privativa && `${r.unidade.area_privativa} m² priv.`, r.unidade?.area_total && `${r.unidade.area_total} m² total`].filter(Boolean).join(' · ')} />
        </div>
        {(r.adendos || []).length > 0 && (
          <div className="mt-2 bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 space-y-1">
            {r.adendos.map((a, i) => <div key={i}>• {a}</div>)}
          </div>
        )}
        {(r._alertas || []).length > 0 && (
          <div className="mt-2">
            <button onClick={() => setVerAlertas((v) => !v)} className="text-xs font-medium text-amber-700 hover:text-amber-900">
              {verAlertas ? '▾' : '▸'} {r._alertas.length} ponto(s) a conferir na leitura do contrato
            </button>
            {verAlertas && (
              <div className="mt-1 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 space-y-1">
                {r._alertas.map((a, i) => <div key={i}>• {a}</div>)}
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
}

function ContratoModal({ contratoId, onClose }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingCS, setSendingCS] = useState(false);
  const [cancelingCS, setCancelingCS] = useState(false);
  const [csError, setCsError] = useState('');
  const [uploadingRev, setUploadingRev] = useState(false);
  const [revError, setRevError] = useState('');
  const revFileInputRef = useRef(null);

  const { data: raw, isLoading } = useQuery({
    queryKey: ['contrato', contratoId],
    queryFn: () => api.get(`/contratos/${contratoId}`).then(r => r.data.data),
  });

  const c = raw;
  const incorp = incorporadoraDe(c?.empreendimento_nome);

  const startEdit = () => { setForm({ numero_contrato: c.numero_contrato, valor_total: c.valor_total, observacoes: c.observacoes || '' }); setEditing(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/contratos/${contratoId}`, form);
      qc.invalidateQueries(['contrato', contratoId]);
      qc.invalidateQueries(['contratos']);
      setEditing(false);
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const handleUploadRevisado = async (file) => {
    if (!file) return;
    setUploadingRev(true); setRevError('');
    try {
      const fd = new FormData();
      fd.append('arquivo_revisado', file);
      await api.post(`/contratos/${contratoId}/revisado`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      qc.invalidateQueries(['contrato', contratoId]);
      qc.invalidateQueries(['contratos']);
    } catch (err) {
      setRevError(err.response?.data?.error || err.message);
    } finally {
      setUploadingRev(false);
      if (revFileInputRef.current) revFileInputRef.current.value = '';
    }
  };

  const handleRemoverRevisado = async () => {
    if (!window.confirm('Remover a minuta revisada? O envio ao Clicksign volta a usar o contrato padrão, gerado automaticamente do modelo.')) return;
    setUploadingRev(true); setRevError('');
    try {
      await api.delete(`/contratos/${contratoId}/revisado`);
      qc.invalidateQueries(['contrato', contratoId]);
      qc.invalidateQueries(['contratos']);
    } catch (err) {
      setRevError(err.response?.data?.error || err.message);
    } finally { setUploadingRev(false); }
  };

  const handleClicksign = async () => {
    const usaRevisado = !!c.arquivo_revisado;
    if (!window.confirm(`Enviar o contrato ${c.numero_contrato} para assinatura no Clicksign?\n\n${usaRevisado ? '⚠️ Será enviada a MINUTA REVISADA (upload manual), não a gerada automaticamente.\n\n' : ''}Signatários (via WhatsApp/e-mail):\n• Leonardo Fagner Botega (Vendedora — ${incorp.razao_social.split(' ')[0]})\n• ${c.cliente_nome} (Comprador)\n${c.conjuge_nome ? `• ${c.conjuge_nome} (Cônjuge)\n` : ''}• ${c.corretor_nome || '—'} (Testemunha 1 — Corretor)\n• Fernando Estrada Perazzolo (Testemunha 2 — Inbase)`)) return;
    setSendingCS(true); setCsError('');
    try {
      await api.post(`/contratos/${contratoId}/clicksign`);
      qc.invalidateQueries(['contrato', contratoId]);
      qc.invalidateQueries(['contratos']);
    } catch (err) {
      setCsError(err.response?.data?.error || err.message);
    } finally { setSendingCS(false); }
  };

  const handleCancelarClicksign = async () => {
    if (!window.confirm(`Cancelar o contrato ${c.numero_contrato} no Clicksign?\n\nO envelope é cancelado para todos os signatários e o contrato volta a ficar editável para você corrigir e reenviar.\n\nSó funciona enquanto ninguém finalizou a assinatura.`)) return;
    setCancelingCS(true); setCsError('');
    try {
      await api.post(`/contratos/${contratoId}/clicksign-cancelar`);
      qc.invalidateQueries(['contrato', contratoId]);
      qc.invalidateQueries(['contratos']);
    } catch (err) {
      setCsError(err.response?.data?.error || err.message);
    } finally { setCancelingCS(false); }
  };

  const [avisandoFin, setAvisandoFin] = useState(false);
  const handleAvisarFinanceiro = async () => {
    if (!window.confirm('Enviar ao financeiro (Alexandre) o WhatsApp com os dados do comprador, valores e datas dos boletos deste contrato?')) return;
    setAvisandoFin(true);
    try {
      await api.post(`contratos/${contratoId}/avisar-financeiro`);
      alert('Aviso enviado ao financeiro no WhatsApp.');
    } catch (err) {
      alert('Erro ao avisar financeiro: ' + (err.response?.data?.error || err.message));
    } finally { setAvisandoFin(false); }
  };

  const [regerando, setRegerando] = useState(false);
  const handleRegerarMinuta = async () => {
    if (!window.confirm('Regerar a minuta (.docx) deste contrato com os valores atuais da reserva?')) return;
    setRegerando(true);
    try {
      await api.post(`contratos/${contratoId}/regerar-minuta`);
      qc.invalidateQueries(['contrato', contratoId]);
      alert('Minuta regerada com os valores atuais. Baixe o DOCX para conferir.');
    } catch (err) {
      alert('Erro ao regerar minuta: ' + (err.response?.data?.error || err.message));
    } finally { setRegerando(false); }
  };

  const [cadCliente, setCadCliente] = useState(false);
  const handleCadastrarCliente = async () => {
    setCadCliente(true);
    try {
      const r = await api.post(`contratos/${contratoId}/cadastrar-cliente`);
      qc.invalidateQueries(['contrato', contratoId]);
      qc.invalidateQueries(['contratos']);
      qc.invalidateQueries(['clientes']);
      alert(r.data.criado
        ? `Cliente "${r.data.data.nome}" cadastrado em Clientes.`
        : `"${r.data.data.nome}" já estava em Clientes — vínculo com este contrato atualizado.`);
    } catch (err) {
      alert('Erro ao cadastrar cliente: ' + (err.response?.data?.error || err.message));
    } finally { setCadCliente(false); }
  };

  const [excluindo, setExcluindo] = useState(false);
  const handleExcluir = async () => {
    const temClicksign = !!c.clicksign_key;
    if (!window.confirm(`Excluir a minuta ${c.numero_contrato}?\n\n${temClicksign ? 'O envelope no Clicksign será cancelado e a minuta será apagada. ' : ''}Isso não pode ser desfeito — se a reserva de origem ainda existir, ela volta a ficar disponível para gerar um novo contrato.`)) return;
    setExcluindo(true); setCsError('');
    try {
      await api.delete(`/contratos/${contratoId}`);
      qc.invalidateQueries(['contratos']);
      onClose();
    } catch (err) {
      setCsError(err.response?.data?.error || err.message);
    } finally { setExcluindo(false); }
  };

  const sim = c?.simulacao_json || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-end">
      <div className="bg-white h-full w-full max-w-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-200 transition"><X size={20}/></button>
            {isLoading ? (
              <span className="text-gray-400 text-sm">Carregando...</span>
            ) : (
              <div>
                <div className="font-bold text-gray-900 text-base">{c?.numero_contrato}</div>
                <div className="text-xs text-gray-500">{c?.empreendimento_nome} — Unidade {formatUnidade(c?.unidade_numero, c?.torre)}</div>
              </div>
            )}
          </div>
          {!isLoading && c && (
            <div className="flex gap-2">
              {!ehLegado(c) && (
              <button onClick={() => downloadDocx(api, `contratos/${contratoId}/download`, nomeMinutaArquivo(c))}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                <Download size={13}/> DOCX
              </button>
              )}
              {temPdfAssinado(c) && (
                <button onClick={() => downloadDocx(api, `contratos/${contratoId}/clicksign-assinado`, c.arquivo_assinado_pdf_nome || `${c.numero_contrato}-assinado.pdf`)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition">
                  <Download size={13}/> PDF assinado
                </button>
              )}
              {['closed', 'finished'].includes(c.clicksign_status) && (
                <button onClick={handleAvisarFinanceiro} disabled={avisandoFin}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50">
                  <Send size={13}/> {avisandoFin ? 'Enviando...' : 'Avisar financeiro'}
                </button>
              )}
              {!c.clicksign_key && !c.arquivo_revisado && !ehLegado(c) && (
                <button onClick={handleRegerarMinuta} disabled={regerando}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50">
                  <Edit3 size={13}/> {regerando ? 'Regerando...' : 'Regerar minuta'}
                </button>
              )}
              {c.status !== 'assinado' && !['closed', 'finished'].includes(c.clicksign_status) && (
                <button onClick={handleExcluir} disabled={excluindo}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition disabled:opacity-50">
                  <Trash2 size={13}/> {excluindo ? 'Excluindo...' : 'Excluir minuta'}
                </button>
              )}
              {c.arquivo_revisado && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-purple-700 bg-purple-50 border border-purple-200 rounded-lg">
                  <Edit3 size={13}/> Revisado
                </span>
              )}
              {!c.clicksign_key && (
                editing ? (
                  <>
                    <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-primary rounded-lg hover:bg-primary-dark transition">
                      <Save size={13}/> {saving ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button onClick={() => setEditing(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                      Cancelar
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={startEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                      <Edit3 size={13}/> Editar
                    </button>
                    {!ehLegado(c) && <button onClick={handleClicksign} disabled={sendingCS}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition">
                      <Send size={13}/> {sendingCS ? 'Enviando...' : 'Enviar Clicksign'}
                    </button>}
                  </>
                )
              )}
              {c.clicksign_key && (
                <>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle size={13}/> {c.clicksign_status === 'closed' ? 'Assinado' : 'Enviado ao Clicksign'}
                  </span>
                  {!['closed', 'finished'].includes(c.clicksign_status) && c.status !== 'assinado' && (
                    <button onClick={handleCancelarClicksign} disabled={cancelingCS}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition">
                      <Ban size={13}/> {cancelingCS ? 'Cancelando...' : 'Cancelar no Clicksign'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Erro Clicksign */}
        {csError && (
          <div className="mx-6 mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-xs shrink-0">
            <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
            {csError}
          </div>
        )}

        {/* Body */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Carregando...</div>
        ) : !c ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Contrato não encontrado</div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">

            {/* Dados editáveis */}
            {editing && (
              <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                <h3 className="text-xs font-bold text-blue-700 uppercase tracking-wider">Editar Contrato</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Nº Contrato</label>
                    <input className="input-field text-sm" value={form.numero_contrato || ''} onChange={e => setForm(f => ({...f, numero_contrato: e.target.value}))} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Valor Total (R$)</label>
                    <input className="input-field text-sm" type="number" step="0.01" value={form.valor_total || ''} onChange={e => setForm(f => ({...f, valor_total: e.target.value}))} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Observações</label>
                  <textarea className="input-field text-sm" rows={3} value={form.observacoes || ''} onChange={e => setForm(f => ({...f, observacoes: e.target.value}))} />
                </div>
              </section>
            )}

            {ehLegado(c) && <ResumoLegado resumo={c.resumo} />}

            {!ehLegado(c) && (<>
            {/* Minuta Revisada — upload manual pra substituir a gerada automaticamente no envio ao Clicksign */}
            <section>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Minuta Revisada</h3>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
                {c.clicksign_key ? (
                  <p className="text-sm text-gray-600">
                    {c.arquivo_revisado
                      ? 'Este contrato foi enviado ao Clicksign usando a minuta revisada abaixo.'
                      : 'Este contrato foi enviado ao Clicksign usando a minuta padrão (gerada automaticamente).'}
                  </p>
                ) : (
                  <p className="text-sm text-gray-600">
                    Baixe o DOCX padrão acima, ajuste algum termo negociado com o cliente e suba o arquivo revisado aqui.
                    Ao clicar em <strong>"Enviar Clicksign"</strong>, essa versão é usada no lugar da gerada automaticamente.
                  </p>
                )}

                {c.arquivo_revisado ? (
                  <div className="flex items-center justify-between bg-white border border-purple-200 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{c.arquivo_revisado_nome_original || 'Minuta revisada'}</div>
                      <div className="text-xs text-gray-400">
                        Enviado {fmtData(c.revisado_em)}{c.revisado_por ? ` por ${c.revisado_por}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      <button onClick={() => downloadDocx(api, `contratos/${contratoId}/download?revisado=1`, (c.arquivo_revisado_nome_original || nomeMinutaArquivo(c)))}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition">
                        <Download size={13}/> Baixar
                      </button>
                      {!c.clicksign_key && (
                        <button onClick={handleRemoverRevisado} disabled={uploadingRev}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition">
                          <Trash2 size={13}/> Remover
                        </button>
                      )}
                    </div>
                  </div>
                ) : !c.clicksign_key && (
                  <div>
                    <input ref={revFileInputRef} type="file" accept=".docx,.doc,.pdf"
                      onChange={e => handleUploadRevisado(e.target.files?.[0])} disabled={uploadingRev}
                      className="block w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-purple-300 file:text-xs file:font-medium file:bg-white file:text-purple-700 hover:file:bg-purple-100" />
                    {uploadingRev && <div className="text-xs text-gray-400 mt-1">Enviando...</div>}
                  </div>
                )}
                {revError && <div className="text-xs text-red-600">{revError}</div>}
              </div>
            </section>

            </>)}

            {/* Unidade */}
            <section>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Unidade</h3>
              <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-3 gap-3 text-sm">
                <div><span className="block text-xs text-gray-400">Empreendimento</span><span className="font-semibold">{c.empreendimento_nome}</span></div>
                <div><span className="block text-xs text-gray-400">Unidade</span><span className="font-semibold">{c.unidade_numero}</span></div>
                <div><span className="block text-xs text-gray-400">Andar</span><span className="font-semibold">{c.andar ? `${c.andar}º` : '—'}</span></div>
                <div><span className="block text-xs text-gray-400">Torre</span><span>{c.torre || '—'}</span></div>
                <div><span className="block text-xs text-gray-400">Área Privativa</span><span>{c.area_privativa ? `${c.area_privativa} m²` : '—'}</span></div>
                <div><span className="block text-xs text-gray-400">Vagas</span><span>{c.num_vagas || '—'}</span></div>
              </div>
            </section>

            {/* Comprador */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Comprador</h3>
                {c.cliente_id ? (
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle size={12}/> Cadastrado em Clientes</span>
                ) : (
                  <button onClick={handleCadastrarCliente} disabled={cadCliente}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800 transition disabled:opacity-50">
                    {cadCliente ? 'Cadastrando...' : '+ Cadastrar cliente no CRM'}
                  </button>
                )}
              </div>
              <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-3 text-sm">
                <div><span className="block text-xs text-gray-400">Nome</span><span className="font-semibold">{c.cliente_nome}</span></div>
                <div><span className="block text-xs text-gray-400">{String(c.cliente_cpf || '').replace(/\D/g, '').length === 14 ? 'CNPJ' : 'CPF'}</span><span>{c.cliente_cpf}</span></div>
                <div><span className="block text-xs text-gray-400">Estado Civil</span><span>{c.cliente_estado_civil}</span></div>
                <div><span className="block text-xs text-gray-400">Profissão</span><span>{c.cliente_profissao}</span></div>
                <div className="col-span-2"><span className="block text-xs text-gray-400">Endereço</span><span>{c.cliente_endereco}</span></div>
                <div><span className="block text-xs text-gray-400">E-mail</span><span>{c.cliente_email}</span></div>
                <div><span className="block text-xs text-gray-400">WhatsApp</span><span>{c.cliente_whatsapp}</span></div>
              </div>
            </section>

            {/* Cônjuge */}
            {c.conjuge_nome && (
              <section>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Cônjuge</h3>
                <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="block text-xs text-gray-400">Nome</span><span className="font-semibold">{c.conjuge_nome}</span></div>
                  <div><span className="block text-xs text-gray-400">CPF</span><span>{c.conjuge_cpf}</span></div>
                  <div><span className="block text-xs text-gray-400">Profissão</span><span>{c.conjuge_profissao}</span></div>
                </div>
              </section>
            )}

            {!ehLegado(c) && (<>
            {/* Cronograma de boletos (reserva_parcelas) — editável, alimenta o aviso ao financeiro */}
            <CronogramaBoletos contratoId={contratoId} />

            {/* Financeiro */}
            {sim.ato && (
              <section>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Fluxo de Pagamento — {c.fluxo_nome}</h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Ato ({fmtData(sim.ato?.data)})</span><span className="font-bold">{fmtBRL(sim.ato?.valor)}</span></div>
                  {sim.mensais?.quantidade > 0 && <div className="flex justify-between"><span className="text-gray-500">{sim.mensais.quantidade}x mensais — 1ª em {fmtData(sim.mensais.data_primeira)}</span><span>{fmtBRL(sim.mensais.valor_mensal)}/mês = {fmtBRL(sim.mensais.total)}</span></div>}
                  {sim.baloes?.quantidade > 0 && <div className="flex justify-between"><span className="text-gray-500">{sim.baloes.quantidade}x reforços ({sim.baloes.frequencia})</span><span>{fmtBRL(sim.baloes.valor_por_balao)} × {sim.baloes.quantidade} = {fmtBRL(sim.baloes.total)}</span></div>}
                  <div className="border-t pt-2 flex justify-between"><span className="text-gray-500">Total fluxo obra</span><span className="font-bold">{fmtBRL(sim.total_obra)}</span></div>
                  <div className="flex justify-between text-primary"><span className="font-semibold">Saldo nas chaves</span><span className="font-bold">{fmtBRL(sim.financiamento_chaves)}</span></div>
                  <div className="flex justify-between font-bold text-gray-800 border-t pt-2"><span>Valor Total</span><span>{fmtBRL(c.valor_total)}</span></div>
                </div>
              </section>
            )}

            {/* Vendedora */}
            <section>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Vendedora</h3>
              <div className="bg-gray-50 rounded-xl p-4 text-sm">
                <p className="font-semibold">{incorp.razao_social}</p>
                <p className="text-gray-500 text-xs">CNPJ: {incorp.cnpj}</p>
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-400 mb-1">Representante — Administrador</p>
                  <p className="font-semibold">Leonardo Fagner Botega</p>
                  <p className="text-gray-500 text-xs">CPF: 048.762.131-38</p>
                  <p className="text-gray-500 text-xs">leonardo@inbaseconstrutora.com · WhatsApp: (44) 99865-2663</p>
                </div>
              </div>
            </section>

            {/* Testemunhas */}
            <section>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Testemunhas</h3>
              <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs font-bold text-gray-400 mb-1">1ª Testemunha — Corretor</p>
                  <p className="font-semibold">{c.corretor_nome || '—'}</p>
                  <p className="text-gray-500 text-xs">{c.corretor_cpf} · CRECI {c.corretor_creci}</p>
                  <p className="text-gray-500 text-xs">{c.corretor_email}</p>
                  <p className="text-gray-500 text-xs">{c.corretor_imobiliaria_nome}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 mb-1">2ª Testemunha</p>
                  <p className="font-semibold">Fernando Estrada Perazzolo</p>
                  <p className="text-gray-400 text-xs">Gerente Comercial</p>
                  <p className="text-gray-500 text-xs">035.406.089-90 · (44) 99137-1919</p>
                  <p className="text-gray-500 text-xs">fernando@inbaseconstrutora.com</p>
                </div>
              </div>
            </section>

            </>)}

            {/* Progresso de assinaturas (só exibe após envio ao Clicksign) */}
            {c.clicksign_key && (
              <SignatariosPanel contratoId={contratoId} clicksignKey={c.clicksign_key} signatariosIniciais={parseSignatarios(c)}
                clicksignStatus={c.clicksign_status} numeroContrato={c.numero_contrato} />
            )}

            {/* Documentos do cliente (anexados no fluxo /reservas) */}
            <section>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{ehLegado(c) ? 'Arquivos do contrato e documentos do cliente' : 'Documentos do cliente'}</h3>
              <DocumentosClienteBox contratoId={contratoId} />
            </section>

            {/* Observações */}
            {(c.observacoes || c.proposta_observacoes) && (
              <section>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Observações</h3>
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap">{c.observacoes || c.proposta_observacoes}</div>
              </section>
            )}

            {/* Timestamps */}
            <div className="text-xs text-gray-400 text-right">
              Gerado em {fmtData(c.created_at)} · Atualizado {fmtData(c.updated_at)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Aba de modelos de contrato ─────────────────────────────────────
function ModelosTab() {
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const fileRef = useRef();

  const { data, isLoading } = useQuery({ queryKey: ['modelos-contrato'], queryFn: () => api.get('/contratos/modelos').then(r => r.data.data) });

  const createMut = useMutation({
    mutationFn: (fd) => api.post('/contratos/modelos', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
    onSuccess: () => { qc.invalidateQueries(['modelos-contrato']); setNome(''); setDescricao(''); if (fileRef.current) fileRef.current.value = ''; alert('Modelo enviado!'); },
    onError: (err) => alert('Erro: ' + (err.response?.data?.error || err.message)),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/contratos/modelos/${id}`),
    onSuccess: () => qc.invalidateQueries(['modelos-contrato']),
  });

  const handleUpload = () => {
    if (!nome || !fileRef.current?.files[0]) return alert('Preencha o nome e selecione um arquivo.');
    const fd = new FormData();
    fd.append('nome', nome); fd.append('descricao', descricao); fd.append('arquivo', fileRef.current.files[0]);
    createMut.mutate(fd);
  };

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-sm text-blue-800 space-y-2">
        <strong className="block mb-1">Variáveis disponíveis no template DOCX:</strong>
        <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 font-mono text-xs">
          <span>{'{ cliente_nome }'}</span><span className="text-gray-500">Nome do comprador</span>
          <span>{'{ cliente_cpf }'}</span><span className="text-gray-500">CPF do comprador</span>
          <span>{'{ cliente_estado_civil }'}</span><span className="text-gray-500">Estado civil</span>
          <span>{'{ cliente_profissao }'}</span><span className="text-gray-500">Profissão</span>
          <span>{'{ cliente_endereco }'}</span><span className="text-gray-500">Endereço completo</span>
          <span>{'{ cliente_email }'}</span><span className="text-gray-500">E-mail do comprador</span>
          <span>{'{ cliente_whatsapp }'}</span><span className="text-gray-500">WhatsApp do comprador</span>
          <span>{'{ conjuge_nome }'}</span><span className="text-gray-500">Nome do cônjuge</span>
          <span>{'{ conjuge_cpf }'}</span><span className="text-gray-500">CPF do cônjuge</span>
          <span>{'{ conjuge_profissao }'}</span><span className="text-gray-500">Profissão do cônjuge</span>
          <span>{'{ empreendimento }'}</span><span className="text-gray-500">Nome do empreendimento</span>
          <span>{'{ unidade }'}</span><span className="text-gray-500">Identificador da unidade</span>
          <span>{'{ torre }'}</span><span className="text-gray-500">Torre</span>
          <span>{'{ andar }'}</span><span className="text-gray-500">Andar</span>
          <span>{'{ area_privativa }'}</span><span className="text-gray-500">Área privativa (m²)</span>
          <span>{'{ num_vagas }'}</span><span className="text-gray-500">Nº de vagas de garagem</span>
          <span>{'{ valor_total }'}</span><span className="text-gray-500">Valor total formatado (R$)</span>
          <span>{'{ data_contrato }'}</span><span className="text-gray-500">Data de geração do contrato</span>
          <span>{'{ fluxo_nome }'}</span><span className="text-gray-500">Nome do fluxo de pagamento</span>
          <span>{'{ fluxo_percentual }'}</span><span className="text-gray-500">% pago durante a obra</span>
          <span>{'{ ato_valor }'}</span><span className="text-gray-500">Valor do ato/entrada</span>
          <span>{'{ ato_data }'}</span><span className="text-gray-500">Data do ato</span>
          <span>{'{ mensais_qtd }'}</span><span className="text-gray-500">Qtd. de parcelas mensais</span>
          <span>{'{ mensais_valor }'}</span><span className="text-gray-500">Valor de cada mensal</span>
          <span>{'{ mensais_data_primeira }'}</span><span className="text-gray-500">Data da 1ª mensal</span>
          <span>{'{ mensais_total }'}</span><span className="text-gray-500">Total das mensais</span>
          <span>{'{ baloes_qtd }'}</span><span className="text-gray-500">Qtd. de reforços/balões</span>
          <span>{'{ baloes_valor_unit }'}</span><span className="text-gray-500">Valor de cada reforço</span>
          <span>{'{ baloes_total }'}</span><span className="text-gray-500">Total dos reforços</span>
          <span>{'{ baloes_frequencia }'}</span><span className="text-gray-500">Frequência dos reforços</span>
          <span>{'{ total_fluxo_obra }'}</span><span className="text-gray-500">Total pago durante a obra</span>
          <span>{'{ financiamento_chaves }'}</span><span className="text-gray-500">Saldo nas chaves</span>
          <span>{'{ corretor_nome }'}</span><span className="text-gray-500">Nome do corretor</span>
          <span>{'{ corretor_cpf }'}</span><span className="text-gray-500">CPF do corretor</span>
          <span>{'{ corretor_creci }'}</span><span className="text-gray-500">CRECI do corretor</span>
          <span>{'{ corretor_email }'}</span><span className="text-gray-500">E-mail do corretor</span>
          <span>{'{ testemunha1_nome }'}</span><span className="text-gray-500">= corretor_nome</span>
          <span>{'{ testemunha1_cpf }'}</span><span className="text-gray-500">= corretor_cpf</span>
          <span>{'{ testemunha1_creci }'}</span><span className="text-gray-500">= corretor_creci</span>
          <span>{'{ testemunha1_email }'}</span><span className="text-gray-500">= corretor_email</span>
          <span>{'{ testemunha1_whatsapp }'}</span><span className="text-gray-500">WhatsApp do corretor</span>
          <span>{'{ testemunha2_nome }'}</span><span className="text-gray-500">Fernando Estrada Perazzolo</span>
          <span>{'{ testemunha2_cargo }'}</span><span className="text-gray-500">Gerente Comercial</span>
          <span>{'{ testemunha2_cpf }'}</span><span className="text-gray-500">035.406.089-90</span>
          <span>{'{ testemunha2_email }'}</span><span className="text-gray-500">fernando@inbaseconstrutora.com</span>
          <span>{'{ testemunha2_whatsapp }'}</span><span className="text-gray-500">(44) 99137-1919</span>
        </div>
        <p className="text-xs text-blue-600 mt-2">⚠️ As âncoras Clicksign {'{{assinatura_X}}'} e {'{{rubrica_X}}'} são adicionadas automaticamente — não precisam ser variáveis do template.</p>
      </div>
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h3 className="font-bold text-sm mb-4">Enviar Novo Modelo</h3>
        <div className="grid grid-cols-3 gap-4 items-end">
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome * (deve ser igual ao empreendimento)</label><input className="input-field" value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Ingarden" /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descrição</label><input className="input-field" value={descricao} onChange={e => setDescricao(e.target.value)} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Arquivo .docx *</label><input type="file" ref={fileRef} accept=".docx" className="text-sm" /></div>
        </div>
        <button onClick={handleUpload} disabled={createMut.isPending} className="btn-primary mt-4 flex items-center gap-2">
          <Upload size={16}/> {createMut.isPending ? 'Enviando...' : 'Enviar Modelo'}
        </button>
      </div>
      {isLoading ? <div className="text-center text-gray-400 py-8">Carregando...</div> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Nome</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Descrição</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Arquivo</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {(data || []).map(m => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{m.nome}</td>
                  <td className="px-4 py-3 text-gray-500">{m.descricao || '—'}</td>
                  <td className="px-4 py-3"><button onClick={() => downloadDocx(api, `contratos/modelos/${m.id}/download`, m.arquivo_nome)} className="text-blue-600 hover:underline flex items-center gap-1"><Download size={14}/> {m.arquivo_nome}</button></td>
                  <td className="px-4 py-3"><button onClick={() => { if(window.confirm('Remover?')) deleteMut.mutate(m.id); }} className="text-red-500 hover:text-red-700"><Trash2 size={15}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {(data || []).length === 0 && <div className="text-center text-gray-400 py-8">Nenhum modelo cadastrado.</div>}
        </div>
      )}
    </div>
  );
}

// ── Aba de contratos ──────────────────────────────────────────────
// ── Ordenação da listagem ──────────────────────────────────────────
// Sempre abre agrupada por empreendimento e, dentro dele, por torre + número da unidade (numérico:
// 302 antes de 1301). As outras ordens são escolha do usuário na tela e não ficam salvas.
const ORDENS = [
  { id: 'empreendimento', label: 'Empreendimento e unidade' },
  { id: 'cliente', label: 'Nome do cliente' },
  { id: 'unidade', label: 'Número da unidade' },
  { id: 'recentes', label: 'Mais recentes' },
];
const semAcento = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const numUnidade = (c) => { const n = parseInt(String(c.unidade_numero || '').replace(/\D/g, ''), 10); return Number.isNaN(n) ? Infinity : n; };
const torreChave = (c) => (!c.torre || c.torre === 'Única' ? '' : String(c.torre));
const porNome = (a, b) => String(a || '').localeCompare(String(b || ''), 'pt-BR', { sensitivity: 'base' });
const porUnidade = (a, b) => numUnidade(a) - numUnidade(b) || porNome(torreChave(a), torreChave(b));
const porEmpreendimento = (a, b) => {
  // contrato sem empreendimento/unidade vai pro fim
  if (!a.empreendimento_nome !== !b.empreendimento_nome) return a.empreendimento_nome ? -1 : 1;
  return porNome(a.empreendimento_nome, b.empreendimento_nome)
    || porNome(torreChave(a), torreChave(b)) || porUnidade(a, b) || porNome(a.cliente_nome, b.cliente_nome);
};
const COMPARADORES = {
  empreendimento: porEmpreendimento,
  cliente: (a, b) => porNome(a.cliente_nome, b.cliente_nome) || porEmpreendimento(a, b),
  unidade: (a, b) => porUnidade(a, b) || porNome(a.empreendimento_nome, b.empreendimento_nome),
  recentes: (a, b) => new Date(b.created_at) - new Date(a.created_at),
};

function ContratosAssinadosTab() {
  const [search, setSearch] = useState('');
  const [empFilter, setEmpFilter] = useState('');
  const [ordem, setOrdem] = useState('empreendimento');
  const [openId, setOpenId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [excluindoId, setExcluindoId] = useState(null);
  const qc = useQueryClient();

  const handleSendClicksign = async (c) => {
    if (!window.confirm(`Enviar "${c.numero_contrato}" para o Clicksign?\n\nO comprador, corretor e testemunhas receberão um e-mail para assinar.`)) return;
    setSendingId(c.id);
    try {
      await api.post(`contratos/${c.id}/clicksign`);
      qc.invalidateQueries(['contratos']);
      setOpenId(c.id); // abre o modal para ver o progresso
    } catch (err) {
      alert('Erro ao enviar ao Clicksign: ' + (err.response?.data?.error || err.message));
    } finally { setSendingId(null); }
  };

  const handleExcluir = async (c) => {
    const temClicksign = !!c.clicksign_key;
    if (!window.confirm(`Excluir a minuta ${c.numero_contrato}?\n\n${temClicksign ? 'O envelope no Clicksign será cancelado e a minuta será apagada. ' : ''}Isso não pode ser desfeito — se a reserva de origem ainda existir, ela volta a ficar disponível para gerar um novo contrato.`)) return;
    setExcluindoId(c.id);
    try {
      await api.delete(`/contratos/${c.id}`);
      qc.invalidateQueries(['contratos']);
    } catch (err) {
      alert('Erro ao excluir minuta: ' + (err.response?.data?.error || err.message));
    } finally { setExcluindoId(null); }
  };

  const { data: empreendimentos } = useQuery({ queryKey: ['empreendimentos-list'], queryFn: () => api.get('/empreendimentos').then(r => r.data.data) });
  const { data, isLoading } = useQuery({
    // busca e filtro no próprio navegador (lista é pequena): dá pra achar por unidade/nº do contrato também
    queryKey: ['contratos'],
    queryFn: () => api.get('/contratos').then(r => r.data.data),
    refetchInterval: 60000, // acompanha o avanço das assinaturas (o webhook do Clicksign atualiza o banco)
  });

  const lista = useMemo(() => {
    const termo = semAcento(search.trim());
    const termoDig = search.replace(/\D/g, '');
    const filtrados = (data || []).filter((c) => {
      if (empFilter && c.empreendimento_id !== empFilter) return false;
      if (!termo) return true;
      const unidade = semAcento(formatUnidade(c.unidade_numero, c.torre));
      return semAcento(c.cliente_nome).includes(termo)
        || unidade === termo || unidade.startsWith(termo)
        || semAcento(c.numero_contrato).includes(termo)
        || semAcento(c.empreendimento_nome).includes(termo)
        || (termoDig.length >= 3 && String(c.cliente_cpf || '').replace(/\D/g, '').includes(termoDig));
    });
    return [...filtrados].sort(COMPARADORES[ordem] || porEmpreendimento);
  }, [data, search, empFilter, ordem]);

  const getStatusBadge = (c) => {
    if (ehLegado(c) && c.status === 'distratado') return <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs font-bold" title="Contrato antigo, distratado (histórico)"><Ban size={12}/> Distratado (legado)</span>;
    if (ehLegado(c)) return <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs font-bold" title="Assinado fora do sistema (contrato + quadro resumo), importado"><CheckCircle size={12}/> Assinado (legado)</span>;
    if (c.clicksign_status === 'closed') return <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs font-bold"><CheckCircle size={12}/> Assinado</span>;
    if (c.clicksign_key) return <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs font-bold"><Clock size={12}/> Aguardando Assinatura</span>;
    if (c.status === 'aguardando_confirmacao') return <span className="flex items-center gap-1 text-orange-600 bg-orange-50 px-2 py-1 rounded-full text-xs font-bold"><FileText size={12}/> Gerado — Confirmar</span>;
    return <span className="text-gray-400 bg-gray-50 px-2 py-1 rounded-full text-xs font-bold">Pendente</span>;
  };

  return (
    <div>
      <div className="flex gap-4 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-3 text-gray-400"/>
          <input className="input-field pl-10" placeholder="Buscar por cliente, CPF/CNPJ, unidade (ex.: 1301, 702B) ou nº do contrato..." value={search} onChange={e => setSearch(e.target.value)}/>
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-3 text-gray-400"><X size={16}/></button>}
        </div>
        <select className="input-field w-56" value={empFilter} onChange={e => setEmpFilter(e.target.value)}>
          <option value="">Todos empreendimentos</option>
          {(empreendimentos || []).map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        <select className="input-field w-60" value={ordem} onChange={e => setOrdem(e.target.value)} title="Organizar por">
          {ORDENS.map(o => <option key={o.id} value={o.id}>Organizar por: {o.label}</option>)}
        </select>
      </div>
      {!isLoading && (
        <div className="text-xs text-gray-500 mb-2">{lista.length} contrato{lista.length === 1 ? '' : 's'}{(search || empFilter) && data ? ` de ${data.length}` : ''}</div>
      )}

      {isLoading ? <div className="text-center text-gray-400 py-8">Carregando...</div> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Nº Contrato</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Cliente</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Empreendimento</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Unidade</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Status</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Valor</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {lista.map((c, i) => (
                <React.Fragment key={c.id}>
                {ordem === 'empreendimento' && (i === 0 || lista[i - 1].empreendimento_nome !== c.empreendimento_nome) && (
                  <tr className="bg-gray-100">
                    <td colSpan={7} className="px-4 py-2 text-xs font-bold text-gray-600 uppercase tracking-wider">
                      {c.empreendimento_nome || 'Sem empreendimento'}
                      <span className="ml-2 font-normal normal-case text-gray-400">
                        {lista.filter(x => x.empreendimento_nome === c.empreendimento_nome).length} contrato(s)
                      </span>
                    </td>
                  </tr>
                )}
                <tr className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setOpenId(c.id)}
                      className="font-bold text-primary hover:text-primary-dark hover:underline text-sm"
                      title="Visualizar / editar contrato"
                    >
                      {c.numero_contrato || '—'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{c.cliente_nome}</div>
                    <div className="text-xs text-gray-500">{c.cliente_cpf}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.empreendimento_nome || '—'}</td>
                  <td className="px-4 py-3 font-medium">{c.unidade_numero ? formatUnidade(c.unidade_numero, c.torre) : '—'}</td>
                  <td className="px-4 py-3 align-top">
                    {getStatusBadge(c)}
                    {c.clicksign_key && c.clicksign_status !== 'closed' && (() => {
                      const sigs = parseSignatarios(c);
                      if (!sigs.length) return null;
                      const assinaram = sigs.filter(s => s.signed_at).length;
                      const faltam = sigs.filter(s => !s.signed_at);
                      return (
                        <div className="mt-1.5 space-y-0.5">
                          <div className="text-[11px] font-semibold text-gray-500">{assinaram}/{sigs.length} assinaram</div>
                          {faltam.map((s, i) => (
                            <div key={i} className="flex items-center gap-1 text-[11px] text-gray-500" title={`${s.nome} — ${papelLabel(s.papel)} (pendente)`}>
                              <Clock size={10} className="text-orange-400 shrink-0"/>
                              <span className="truncate max-w-[180px]">{primeiroNome(s.nome)} · {papelLabel(s.papel)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-700">{c.valor_total ? parseFloat(c.valor_total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setOpenId(c.id)} title="Ver detalhes"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition">
                        <FileText size={14}/>
                      </button>
                      {!ehLegado(c) && (
                      <button onClick={() => downloadDocx(api, `contratos/${c.id}/download`, nomeMinutaArquivo(c))} title="Baixar DOCX"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition">
                        <Download size={14}/>
                      </button>
                      )}
                      {ehLegado(c) ? null : !c.clicksign_key ? (
                        <button onClick={() => handleSendClicksign(c)} disabled={sendingId === c.id} title="Enviar ao Clicksign"
                          className="p-1.5 rounded-lg text-blue-400 hover:text-blue-700 hover:bg-blue-50 transition">
                          {sendingId === c.id
                            ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                            : <Send size={14}/>}
                        </button>
                      ) : (
                        <button onClick={() => setOpenId(c.id)} title={c.clicksign_status === 'closed' ? 'Totalmente assinado' : 'Ver progresso de assinaturas'}
                          className={`p-1.5 rounded-lg transition ${c.clicksign_status === 'closed' ? 'text-green-500 hover:bg-green-50' : 'text-orange-400 hover:bg-orange-50'}`}>
                          <CheckCircle size={14}/>
                        </button>
                      )}
                      {temPdfAssinado(c) && (
                        <button onClick={() => downloadDocx(api, `contratos/${c.id}/clicksign-assinado`, c.arquivo_assinado_pdf_nome || `${c.numero_contrato}-assinado.pdf`)}
                          title="Baixar contrato assinado (PDF)"
                          className="p-1.5 rounded-lg text-green-600 hover:text-green-800 hover:bg-green-50 transition">
                          <FileText size={14}/>
                        </button>
                      )}
                      {c.status !== 'assinado' && !['closed', 'finished'].includes(c.clicksign_status) && (
                        <button onClick={() => handleExcluir(c)} disabled={excluindoId === c.id} title="Excluir minuta"
                          className="p-1.5 rounded-lg text-red-400 hover:text-red-700 hover:bg-red-50 transition disabled:opacity-50">
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
          {lista.length === 0 && <div className="text-center text-gray-400 py-8">Nenhum contrato encontrado.</div>}
        </div>
      )}

      {openId && <ContratoModal contratoId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

// ── Export principal ──────────────────────────────────────────────
export default function ContratosList() {
  const [tab, setTab] = useState('contratos');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-800">Contratos</h1>
      </div>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab('contratos')} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === 'contratos' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <FileText size={14} className="inline mr-1"/> Contratos
        </button>
        <button onClick={() => setTab('modelos')} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === 'modelos' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <Upload size={14} className="inline mr-1"/> Modelos de Contrato
        </button>
      </div>
      {tab === 'contratos' ? <ContratosAssinadosTab /> : <ModelosTab />}
    </div>
  );
}
